<template>
	<DialogV2
		ref="dialog" 
		:title="$t('text.addProvision')"
		:cancelLabel="$t('text.cancel')"
		:confirmLabel="$t('text.confirm')"
		:onConfirm="confirm"
		:disableConfirmButton="disabledConfirmButton"
		:fullHeight="true"
	>
		<template #content>
			<div class="SpecificProvisionDialog common-form">
				<loading :active="loading" :is-full-page="false" color="#4caf50" data-cy="loading" />
				<FieldSet class="firstOfType" :label="$t('text.addProvision')" :info-text="$t('text.addSpecificProvisionInfoText')">
					<Field v-model="model.provision" typeName="SpecificProvision" fieldName="provision" dataCy="provision"
						:info-text="$t('text.provisionInfoText')" placeholder="0.00" />
				</FieldSet>
				<FieldSet :label="$t('text.reseller')" :info-text="$t('text.specificProvisionsResellerInfoText')">
					<Field v-model="model.resellers" typeName="SpecificProvision" fieldName="resellers" dataCy="resellers"
						:show-title="false" :placeholder="`${$t('text.chooseReseller')}...`" :options="tourOperators" />
				</FieldSet>
				<FieldSet :label="$t('text.productGroups')" :info-text="$t('text.specificProvisionsProductGroupsInfoText')">
					<Field v-model="model.productGroups" typeName="SpecificProvision" fieldName="productGroups" dataCy="resellers"
						:show-title="false" :placeholder="`${$t('text.chooseProductGroups')}...`" :options="productGroups" />
				</FieldSet>
				<FieldSet :label="$t('text.products')" :info-text="$t('text.specificProvisionsProductsInfoText')">
					<table class="products-table">
						<thead>
							<th>{{ $t('text.type') }}</th>
							<th>{{ $t('text.service') }}</th>
							<th>{{ $t('text.activation') }}</th>
							<th></th>
						</thead>
						<tbody>
							<tr class="empty-row" v-if="!products.length">
								<td>{{ $t('text.noProductsAdded') }}</td>
							</tr>
							<template v-else>
								<tr v-for="product of products" :key="product.product_id">
									<td><v-icon style="color: #8F8F8F;">mdi-ticket</v-icon></td>
									<td><p>{{ product.product_sku }}</p><p>{{ product.product_name }}</p></td>
									<td><v-icon v-if="product.product_status === 'activated'" style="color: #64c823;">mdi-check</v-icon><v-icon v-else style="color: #f34545;">mdi-close</v-icon></td>
									<td><v-icon @click="deleteProduct(product.product_id)">mdi-delete</v-icon></td>
								</tr>
							</template>
						</tbody>
					</table>
					<div class="add-product-button">
						<v-btn class="gradientButton" elevation="0" @click="openProductsDialog">
						<v-icon>mdi-plus</v-icon>
						{{ $t('text.addProvision') }}
					</v-btn>
					</div>
				</FieldSet>
				<ProductsDialog ref="productsDialog"
					v-model="model.products"
					:service-provider-id="serviceProviderId"
					:attribute-sets="productGroups"
					:help-text="$t('text.provisionProductsDialogHelpText')"
					:skip-search="!serviceProviderId"
					@update:modelValue="updateSelected($event)"
				/>
			</div>
		</template>
	</DialogV2>
</template>

<script>
import Common from '@/mixins/Common.vue'
import FieldSet from '../../views/applications/packageDesigner/FieldSet.vue'
import Field from '@/components/fields/Field.vue'
import ProductsDialog from '../../views/applications/packageDesigner/dialogs/ProductsDialog.vue'
import Loading from 'vue-loading-overlay'
import DialogV2 from '../common/DialogV2.vue'

export default {
	name: 'SpecificProvisionDialog',
	components: { FieldSet, Field, ProductsDialog, Loading, DialogV2 },
	mixins: [Common],
	props: {
		modelValue: { type: Object, required: true },
		tourOperators: { type: Array, required: true },
		productGroups: { type: Array, required: true },
		serviceProviderId: { type: String, required: true },
		clientId: { type: String, required: true },
	},
	data() {
		return {
			model: {},
			loading: false,
			products: []
		}
	},
	computed: {
		disabledConfirmButton() {
			return !this.model.resellers?.length || (this.model.provision !== 0 && (!this.model.provision || isNaN(this.model.provision)))
		}
	},
	methods: {
		open() {
			this.model = JSON.parse(JSON.stringify(this.modelValue))
			this.getProducts()
			this.$refs.dialog.open()
		},
		confirm() {
			this.$emit('update:modelValue', this.model)
			this.close()
		},
		close() {
			this.model = {}
			this.products = []
		},
		deleteProduct(id) {
			this.model.products = this.model.products.filter(x => x !== id)
			this.updateSelected(this.model.products)
		},
		openProductsDialog() {
			this.$refs.productsDialog.open()
		},
		updateSelected(selected) {
			this.model.products = selected

			if (!selected?.length) {
				this.products = []
				return
			}

			const existingIds = this.products.map(x => x.product_id)
			const missingIds = selected.filter(id => !existingIds.includes(id))

			if (missingIds.length) {
				this.getProducts()
			} else {
				this.products = this.products.filter(x => selected.includes(x.product_id))
			}
		},
		async getProducts() {
			if (this.model.products?.length) {
				this.loading = true
				const productFilters = {
					serviceProviderId: this.serviceProviderId,
					productIds: this.model.products
				} 
				const r = await this.$httpPost(`/packageTravel/products?clientId=${this.clientId}`, { ...productFilters })
				this.products = r.products
				this.loading = false
			}
		}
	},
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/_common-form.scss';

.SpecificProvisionDialog {
	.FieldSet {
		.Field {
			margin-bottom: 0px;
		}
		&.firstOfType {
			.Field {
				padding-top: 24px;
			}
		}
		&:not(.firstOfType) {
			padding-top: 24px;
		}
		:deep(.TagsFieldCheckbox) {
			margin-top: 4px;
		}
	}
	.products-table {
		width: 100%;
		border-collapse: collapse;
		> thead {
			border-bottom: 1px solid #e5e5e5;
			> th {
				padding: 14px 12px;
				font-size: 15px;
				font-weight: 500;
				line-height: 20px;
				letter-spacing: -0.037px;
				text-align: left;
				&:nth-child(1),
				&:nth-child(3),
				&:nth-child(4) {
					text-align: center;
				}
				&:nth-child(4) {
					width: 48px;
				}
			}
		}
		> tbody {
			> tr {
				border-bottom: 1px solid #e5e5e5;
				height: 60px;
				> td {
					padding: 0px 12px;
					&:nth-child(2) {
						p:first-child {
							color: #8F8F8F;
							font-size: 12px;
							font-weight: 400;
							line-height: 16px;
							letter-spacing: 0.001px;
						}
						p:last-child {
							color: #000;
							font-size: 15px;
							font-weight: 400;
							line-height: 20px;
							letter-spacing: -0.037px;
						}
					}
					&:nth-child(1),
					&:nth-child(3),
					&:nth-child(4) {
						text-align: center;
					}
					&:nth-child(4) {
						width: 48px;
						> i {
							cursor: pointer;
						}
					}
				}
				&.empty-row {
					color: #8F8F8F;
					font-size: 15px;
					font-weight: 400;
					line-height: 20px;
					letter-spacing: -0.037px;
				}
			}
		}
	}
	.add-product-button {
		width: 100%;
		padding: 12px;
		> button {
			width: 100%;
			border-radius: 4px !important;
			border: 1px dashed #424242 !important;
		}
	}
}
</style>